import { buildWorkspaceCandidateMap, compareImportCandidates, getImportCatalog, getModuleExports, getModuleSource, hasKnownModule } from './catalog';
import {
  collectDefinedNames,
  collectUsedIdentifiers,
  detectNewline,
  extractDiagnosticIdentifier,
  findLeadingImportRegion,
  getImportBindingSymbol,
  getLineStarts,
  isMissingImportDiagnostic,
  isNamespaceUsage,
  isUnusedImportDiagnostic,
  maskPythonSource,
  parseTopLevelImports,
} from './sourceAnalysis';
import {
  DiagnosticLike,
  ExplicitImportRequest,
  ImportBinding,
  ImportCandidate,
  ImportStatement,
  MissingImportTarget,
  OffsetTextEdit,
  QuickFixAction,
  WorkspaceModuleLike,
} from './types';

interface ImportServiceOptions {
  diagnostics?: DiagnosticLike[];
  workspaceModules?: WorkspaceModuleLike[];
}

interface ResolvedImportContext {
  code: string;
  workspaceModules: WorkspaceModuleLike[];
  diagnostics: DiagnosticLike[];
}

function uniqueCandidates(candidates: ImportCandidate[]): ImportCandidate[] {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key = [candidate.importType, candidate.module, candidate.importedName ?? '', candidate.alias ?? ''].join(':');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function getImportedBindingMap(importStatements: ImportStatement[]): Map<string, ImportStatement[]> {
  const importedBindings = new Map<string, ImportStatement[]>();

  for (const importStatement of importStatements) {
    for (const binding of importStatement.bindings) {
      const bindingSymbol = getImportBindingSymbol(importStatement, binding);
      const statements = importedBindings.get(bindingSymbol) ?? [];
      statements.push(importStatement);
      importedBindings.set(bindingSymbol, statements);
    }
  }

  return importedBindings;
}

function resolveImportCandidates(symbol: string, isNamespaceImport: boolean, workspaceModules: WorkspaceModuleLike[]): ImportCandidate[] {
  const catalog = getImportCatalog();
  const workspaceCandidates = buildWorkspaceCandidateMap(workspaceModules).get(symbol) ?? [];
  const catalogCandidates = catalog.candidatesBySymbol.get(symbol) ?? [];

  const mergedCandidates = uniqueCandidates([...workspaceCandidates, ...catalogCandidates]).sort(compareImportCandidates);

  if (!isNamespaceImport) {
    return mergedCandidates;
  }

  const namespacePreferred = mergedCandidates.filter((candidate) => candidate.isNamespaceImport || candidate.importType === 'import');
  const namespaceFallback = mergedCandidates.filter((candidate) => !namespacePreferred.includes(candidate));
  return [...namespacePreferred, ...namespaceFallback];
}

function collectMissingImportTargets(context: ResolvedImportContext): MissingImportTarget[] {
  const topLevelImports = parseTopLevelImports(context.code).statements;
  const definedNames = collectDefinedNames(context.code);
  const importedBindings = getImportedBindingMap(topLevelImports);
  const builtins = getImportCatalog().builtins;
  const missingImportTargets = new Map<string, MissingImportTarget>();

  for (const diagnostic of context.diagnostics.filter(isMissingImportDiagnostic)) {
    const symbol = extractDiagnosticIdentifier(context.code, diagnostic);
    if (!symbol || builtins.has(symbol) || definedNames.has(symbol) || importedBindings.has(symbol)) {
      continue;
    }

    if (missingImportTargets.has(symbol)) {
      continue;
    }

    const namespaceImport = isNamespaceUsage(context.code, diagnostic.endOffset);
    const candidates = resolveImportCandidates(symbol, namespaceImport, context.workspaceModules);
    if (candidates.length === 0) {
      continue;
    }

    missingImportTargets.set(symbol, {
      symbol,
      startOffset: diagnostic.startOffset,
      endOffset: diagnostic.endOffset,
      isNamespaceUsage: namespaceImport,
      candidates,
    });
  }

  return [...missingImportTargets.values()].sort((left, right) => left.startOffset - right.startOffset);
}

function formatImportBinding(binding: Pick<ImportBinding, 'name' | 'alias'>): string {
  return binding.alias ? `${binding.name} as ${binding.alias}` : binding.name;
}

function formatFromImportLine(moduleName: string, bindings: Array<Pick<ImportBinding, 'name' | 'alias'>>): string {
  const sortedBindings = [...bindings].sort((left, right) => {
    const leftKey = `${left.name}${left.alias ?? ''}`;
    const rightKey = `${right.name}${right.alias ?? ''}`;
    return leftKey.localeCompare(rightKey);
  });
  return `from ${moduleName} import ${sortedBindings.map(formatImportBinding).join(', ')}`;
}

function getTrailingInlineComment(rawLine: string): string {
  const maskedLine = maskPythonSource(rawLine);
  for (let index = 0; index < rawLine.length; index += 1) {
    if (rawLine[index] === '#' && maskedLine[index] === ' ') {
      const prefix = rawLine.slice(0, index).match(/\s*$/)?.[0] ?? ' ';
      return `${prefix || ' '}${rawLine.slice(index).trimEnd()}`;
    }
  }
  return '';
}

function formatImportLine(moduleName: string): string {
  return `import ${moduleName}`;
}

function formatImportStatementLine(bindings: Array<Pick<ImportBinding, 'name' | 'alias'>>): string {
  const sortedBindings = [...bindings].sort((left, right) => {
    const leftKey = `${left.name}${left.alias ?? ''}`;
    const rightKey = `${right.name}${right.alias ?? ''}`;
    return leftKey.localeCompare(rightKey);
  });

  return `import ${sortedBindings.map(formatImportBinding).join(', ')}`;
}

function alreadyImportsCandidate(importStatement: ImportStatement, candidate: ImportCandidate): boolean {
  if (candidate.importType === 'import') {
    return importStatement.kind === 'import' && importStatement.bindings.some((binding) => binding.name === candidate.module && !binding.alias);
  }

  if (importStatement.kind !== 'from' || importStatement.module !== candidate.module) {
    return false;
  }

  if (importStatement.hasWildcard) {
    return true;
  }

  const importedName = candidate.importedName ?? candidate.symbol;
  return importStatement.bindings.some((binding) => binding.name === importedName && !binding.alias);
}

function buildImportEdits(code: string, candidates: ImportCandidate[]): OffsetTextEdit[] {
  if (candidates.length === 0) {
    return [];
  }

  const newline = detectNewline(code);
  const { statements: topLevelImports } = parseTopLevelImports(code);
  const leadingRegion = findLeadingImportRegion(code, topLevelImports);
  const lineStarts = getLineStarts(code);
  const mergeEdits = new Map<number, OffsetTextEdit>();
  const mergeBindingsByStatement = new Map<number, Array<Pick<ImportBinding, 'name' | 'alias'>>>();
  const newFromImports = new Map<string, string[]>();
  const newImportModules = new Set<string>();

  for (const candidate of candidates) {
    const duplicate = topLevelImports.some((importStatement) => alreadyImportsCandidate(importStatement, candidate));
    if (duplicate) {
      continue;
    }

    if (candidate.importType === 'from') {
      const importedName = candidate.importedName ?? candidate.symbol;
      const mergeTarget = topLevelImports.find(
        (importStatement) => importStatement.kind === 'from' && importStatement.module === candidate.module && !importStatement.hasWildcard
      );

      if (mergeTarget) {
        const bindings = [
          ...(mergeBindingsByStatement.get(mergeTarget.startOffset) ??
            mergeTarget.bindings.map((binding) => ({ name: binding.name, alias: binding.alias }))),
        ];
        if (!bindings.some((binding) => binding.name === importedName && !binding.alias)) {
          bindings.push({ name: importedName });
        }

        mergeBindingsByStatement.set(mergeTarget.startOffset, bindings);
        mergeEdits.set(mergeTarget.startOffset, {
          startOffset: mergeTarget.startOffset,
          endOffset: mergeTarget.endOffset,
          newText: `${formatFromImportLine(mergeTarget.module, bindings)}${getTrailingInlineComment(mergeTarget.raw)}`,
        });
      } else {
        const imports = newFromImports.get(candidate.module) ?? [];
        if (!imports.includes(importedName)) {
          imports.push(importedName);
        }
        newFromImports.set(candidate.module, imports);
      }
      continue;
    }

    newImportModules.add(candidate.module);
  }

  const newImportLines = [
    ...[...newImportModules].sort().map((moduleName) => formatImportLine(moduleName)),
    ...[...newFromImports.entries()]
      .sort(([leftModule], [rightModule]) => leftModule.localeCompare(rightModule))
      .map(([moduleName, importedNames]) =>
        formatFromImportLine(
          moduleName,
          importedNames.map((name) => ({ name }))
        )
      ),
  ];

  const edits = [...mergeEdits.values()];
  if (newImportLines.length === 0) {
    return edits.sort((left, right) => left.startOffset - right.startOffset);
  }

  const insertionOffset =
    leadingRegion.lastImport !== null
      ? leadingRegion.lastImport.endLine + 1 < lineStarts.length
        ? lineStarts[leadingRegion.lastImport.endLine + 1]
        : code.length
      : leadingRegion.insertionOffset;
  const insertionSuffix = leadingRegion.needsTrailingBlankLine ? `${newline}${newline}` : newline;
  edits.push({
    startOffset: insertionOffset,
    endOffset: insertionOffset,
    newText: `${newImportLines.join(newline)}${insertionSuffix}`,
  });

  return edits.sort((left, right) => left.startOffset - right.startOffset);
}

function normalizeServiceOptions(options: ImportServiceOptions = {}): ResolvedImportContext {
  return {
    code: '',
    diagnostics: options.diagnostics ?? [],
    workspaceModules: options.workspaceModules ?? [],
  };
}

function getUnusedImportDiagnostics(diagnostics: DiagnosticLike[]): DiagnosticLike[] {
  return diagnostics.filter(isUnusedImportDiagnostic);
}

function hasUnusedImportDiagnosticForBinding(binding: ImportBinding, diagnostics: DiagnosticLike[]): boolean {
  return diagnostics.some((diagnostic) => diagnostic.startOffset <= binding.endOffset && diagnostic.endOffset >= binding.startOffset);
}

function shouldKeepBinding(
  binding: ImportBinding,
  statement: ImportStatement,
  usedIdentifiers: Set<string>,
  unusedDiagnostics: DiagnosticLike[],
  preferDiagnosticRemoval: boolean
): boolean {
  if (statement.isFuture || statement.hasWildcard) {
    return true;
  }

  const bindingSymbol = getImportBindingSymbol(statement, binding);
  if (preferDiagnosticRemoval) {
    return !hasUnusedImportDiagnosticForBinding(binding, unusedDiagnostics);
  }

  return usedIdentifiers.has(bindingSymbol);
}

function groupRank(moduleName: string, workspaceModules: WorkspaceModuleLike[]): number {
  const source = getModuleSource(moduleName, workspaceModules);
  switch (source) {
    case 'stdlib':
      return 0;
    case 'student-lib':
    case 'sample':
      return 1;
    case 'workspace':
    default:
      return 2;
  }
}

function buildOrganizedImportBlock(
  statements: ImportStatement[],
  workspaceModules: WorkspaceModuleLike[],
  newline: string,
  diagnostics: DiagnosticLike[],
  code: string
): string {
  const unusedDiagnostics = getUnusedImportDiagnostics(diagnostics);
  const preferDiagnosticRemoval = unusedDiagnostics.length > 0;
  const usedIdentifiers = collectUsedIdentifiers(code, statements);
  const futureLines: string[] = [];
  const groupedImportLines = new Map<number, string[]>();

  for (const statement of statements) {
    if (statement.hasWildcard) {
      if (!hasKnownModule(statement.module, workspaceModules)) {
        const groupLines = groupedImportLines.get(groupRank(statement.module, workspaceModules)) ?? [];
        groupLines.push(statement.raw);
        groupedImportLines.set(groupRank(statement.module, workspaceModules), groupLines);
        continue;
      }

      const moduleExports = getModuleExports(statement.module, workspaceModules);
      const usedFromModule = [...moduleExports].filter((symbol) => usedIdentifiers.has(symbol)).sort();

      if (usedFromModule.length > 0) {
        const expandedLine = formatFromImportLine(
          statement.module,
          usedFromModule.map((name) => ({ name }))
        );
        const groupLines = groupedImportLines.get(groupRank(statement.module, workspaceModules)) ?? [];
        groupLines.push(expandedLine);
        groupedImportLines.set(groupRank(statement.module, workspaceModules), groupLines);
      }
      continue;
    }

    const remainingBindings = statement.bindings.filter((binding) =>
      shouldKeepBinding(binding, statement, usedIdentifiers, unusedDiagnostics, preferDiagnosticRemoval)
    );

    if (statement.isFuture) {
      if (remainingBindings.length > 0) {
        futureLines.push(formatFromImportLine(statement.module, remainingBindings));
      } else {
        futureLines.push(formatFromImportLine(statement.module, statement.bindings));
      }
      continue;
    }

    if (remainingBindings.length === 0) {
      continue;
    }

    const line =
      statement.kind === 'import' ? formatImportStatementLine(remainingBindings) : formatFromImportLine(statement.module, remainingBindings);

    const groupLines = groupedImportLines.get(groupRank(statement.module, workspaceModules)) ?? [];
    groupLines.push(line);
    groupedImportLines.set(groupRank(statement.module, workspaceModules), groupLines);
  }

  const sections: string[] = [];
  if (futureLines.length > 0) {
    sections.push(futureLines.join(newline));
  }

  for (const rank of [0, 1, 2]) {
    const lines = groupedImportLines.get(rank)?.sort((left, right) => left.localeCompare(right)) ?? [];
    if (lines.length > 0) {
      sections.push(lines.join(newline));
    }
  }

  return sections.join(`${newline}${newline}`);
}

export function buildAddMissingImportsEdits(code: string, options: ImportServiceOptions = {}): OffsetTextEdit[] {
  const context = normalizeServiceOptions(options);
  context.code = code;
  const missingImportTargets = collectMissingImportTargets(context);
  const topRankedCandidates = missingImportTargets.map((missingImportTarget) => missingImportTarget.candidates[0]).filter(Boolean);
  return buildImportEdits(code, topRankedCandidates);
}

export function buildSingleImportEdits(code: string, candidate: ImportCandidate): OffsetTextEdit[] {
  return buildImportEdits(code, [candidate]);
}

export function buildExplicitImportEdits(code: string, imports: ExplicitImportRequest[]): OffsetTextEdit[] {
  const candidates = imports.flatMap((importRequest) =>
    importRequest.names.map((name) => ({
      symbol: name,
      importType: 'from' as const,
      module: importRequest.module,
      importedName: name,
      priority: 0,
      source: getModuleSource(importRequest.module, []),
    }))
  );

  return buildImportEdits(code, candidates);
}

export function buildMissingImportQuickFixes(
  code: string,
  rangeStartOffset: number,
  rangeEndOffset: number,
  options: ImportServiceOptions = {}
): QuickFixAction[] {
  const context = normalizeServiceOptions(options);
  context.code = code;

  const relevantTargets = collectMissingImportTargets(context).filter(
    (target) => target.startOffset <= rangeEndOffset && target.endOffset >= rangeStartOffset
  );

  const quickFixes: QuickFixAction[] = [];
  for (const target of relevantTargets) {
    target.candidates.forEach((candidate, index) => {
      const title = candidate.importType === 'import' ? `Import ${candidate.module}` : `Import ${target.symbol} from ${candidate.module}`;
      const edits = buildSingleImportEdits(code, candidate);
      if (edits.length === 0) {
        return;
      }

      quickFixes.push({
        title,
        edits,
        isPreferred: index === 0,
      });
    });
  }

  const addMissingImportsEdits = buildAddMissingImportsEdits(code, options);
  if (addMissingImportsEdits.length > 0) {
    quickFixes.push({
      title: 'Add Missing Imports',
      edits: addMissingImportsEdits,
    });
  }

  return quickFixes;
}

export function buildExpandWildcardEdits(code: string, options: ImportServiceOptions = {}): OffsetTextEdit[] {
  const workspaceModules = options.workspaceModules ?? [];
  const { statements: topLevelImports } = parseTopLevelImports(code);
  const wildcardStatements = topLevelImports.filter((statement) => statement.hasWildcard);

  if (wildcardStatements.length === 0) {
    return [];
  }

  const usedIdentifiers = collectUsedIdentifiers(code, topLevelImports);
  const edits: OffsetTextEdit[] = [];

  for (const statement of wildcardStatements) {
    if (!hasKnownModule(statement.module, workspaceModules)) {
      continue;
    }

    const moduleExports = getModuleExports(statement.module, workspaceModules);
    const usedFromModule = [...moduleExports].filter((symbol) => usedIdentifiers.has(symbol)).sort();

    if (usedFromModule.length > 0) {
      const expandedLine = formatFromImportLine(
        statement.module,
        usedFromModule.map((name) => ({ name }))
      );
      edits.push({
        startOffset: statement.startOffset,
        endOffset: statement.endOffset,
        newText: expandedLine,
      });
    } else {
      const lineStarts = getLineStarts(code);
      const nextLineOffset = statement.endLine + 1 < lineStarts.length ? lineStarts[statement.endLine + 1] : code.length;
      edits.push({
        startOffset: statement.startOffset,
        endOffset: nextLineOffset,
        newText: '',
      });
    }
  }

  return edits;
}

export function buildOrganizeImportsFallbackEdits(code: string, options: ImportServiceOptions = {}): OffsetTextEdit[] {
  const workspaceModules = options.workspaceModules ?? [];
  const diagnostics = options.diagnostics ?? [];
  const newline = detectNewline(code);
  const lines = code.split(/\r?\n/);
  const parsedImports = parseTopLevelImports(code);
  const leadingRegion = findLeadingImportRegion(code, parsedImports.statements);
  const leadingStatements = parsedImports.statements.filter(
    (statement) =>
      leadingRegion.firstImport !== null &&
      statement.startOffset >= leadingRegion.firstImport.startOffset &&
      (leadingRegion.lastImport === null || statement.endOffset <= leadingRegion.lastImport.endOffset)
  );

  if (leadingStatements.length === 0 || parsedImports.hasComplexImports) {
    return [];
  }

  // Unsupported wildcard imports must remain exactly in place because
  // reordering adjacent imports can change which bindings win at runtime.
  if (leadingStatements.some((statement) => statement.hasWildcard && !hasKnownModule(statement.module, workspaceModules))) {
    return [];
  }

  const organizedBlock = buildOrganizedImportBlock(leadingStatements, workspaceModules, newline, diagnostics, code);
  const replacementStart = leadingRegion.firstImport?.startOffset ?? 0;
  const lineStarts = getLineStarts(code);
  let replacementEndLine = (leadingRegion.lastImport?.endLine ?? leadingRegion.firstExecutableLine) + 1;
  while (replacementEndLine < leadingRegion.firstExecutableLine && !(lines[replacementEndLine] ?? '').trim()) {
    replacementEndLine += 1;
  }
  const replacementEnd = replacementEndLine < lineStarts.length ? lineStarts[replacementEndLine] : code.length;

  // organizedBlock is rebuilt from parsed bindings alone, so comments attached to
  // import lines or placed between imports would be silently dropped. Let the LSP
  // path handle those cases, while preserving comments after the import block.
  if (code.slice(replacementStart, leadingRegion.lastImport?.endOffset ?? replacementEnd).includes('#')) {
    return [];
  }

  if (!organizedBlock) {
    return replacementStart === replacementEnd
      ? []
      : [
          {
            startOffset: replacementStart,
            endOffset: replacementEnd,
            newText: '',
          },
        ];
  }

  return [
    {
      startOffset: replacementStart,
      endOffset: replacementEnd,
      newText: `${organizedBlock}${replacementEnd < code.length ? `${newline}${newline}` : newline}`,
    },
  ];
}
